import { useState, useEffect } from 'react';

interface FarmGameProps {
  currentUser: { name: string; role: string } | null;
  onOpenAuth: () => void;
  userScore: number;
  compact?: boolean;
}

interface Crop {
  id: string;
  name: string;
  emoji: string;
  requiredScore: number;
  growthStages: string[];
  harvestScore: number;
  color: string;
  bgColor: string;
  season: Season[];
}

type Season = 'spring' | 'summer' | 'autumn' | 'winter';

interface PlotState {
  id: number;
  cropId: string | null;
  stage: number;
  plantedAt: number | null;
  watered: boolean;
}

interface FarmLevel {
  level: number;
  name: string;
  emoji: string;
  requiredHarvests: number;
  plotCount: number;
  color: string;
  bg: string;
  perks: string;
}

const FARM_LEVELS: FarmLevel[] = [
  { level: 1, name: '新手农夫', emoji: '🌱', requiredHarvests: 0,  plotCount: 9,  color: 'text-green-600',  bg: 'bg-green-50',  perks: '9块农田' },
  { level: 2, name: '勤劳农民', emoji: '🌿', requiredHarvests: 10, plotCount: 12, color: 'text-emerald-600', bg: 'bg-emerald-50', perks: '12块农田 + 浇水加速' },
  { level: 3, name: '丰收达人', emoji: '🌾', requiredHarvests: 25, plotCount: 16, color: 'text-amber-600',   bg: 'bg-amber-50',  perks: '16块农田 + 双倍积分' },
  { level: 4, name: '农场主',   emoji: '🏡', requiredHarvests: 50, plotCount: 20, color: 'text-orange-600', bg: 'bg-orange-50', perks: '20块农田 + 稀有作物' },
  { level: 5, name: '农业大师', emoji: '👑', requiredHarvests: 100,plotCount: 25, color: 'text-rose-600',   bg: 'bg-rose-50',   perks: '25块农田 + 全季节作物' },
];

const SEASON_CONFIG: Record<Season, { label: string; emoji: string; gradFrom: string; gradTo: string; bg: string; desc: string }> = {
  spring: { label: '春季', emoji: '🌸', gradFrom: '#ec4899', gradTo: '#f43f5e', bg: 'linear-gradient(135deg,#7B5E3A,#9B7A52,#7B5E3A)', desc: '万物复苏，嫩芽蔬菜最旺盛' },
  summer: { label: '夏季', emoji: '☀️', gradFrom: '#f59e0b', gradTo: '#f97316', bg: 'linear-gradient(135deg,#6B4E1A,#8B6A2A,#6B4E1A)', desc: '阳光充足，瓜果大丰收' },
  autumn: { label: '秋季', emoji: '🍂', gradFrom: '#f97316', gradTo: '#f59e0b', bg: 'linear-gradient(135deg,#8B5E2A,#A0723A,#8B5E2A)', desc: '金秋收获，根茎类最旺' },
  winter: { label: '冬季', emoji: '❄️', gradFrom: '#38bdf8', gradTo: '#06b6d4', bg: 'linear-gradient(135deg,#5A6E7A,#7A8E9A,#5A6E7A)', desc: '寒冬特供，耐寒蔬菜登场' },
};

const ALL_CROPS: Crop[] = [
  { id: 'carrot',     name: '胡萝卜', emoji: '🥕', requiredScore: 0,   growthStages: ['🌱','🌿','🌾','🥕'], harvestScore: 30,  color: 'text-orange-600', bgColor: 'bg-orange-50', season: ['spring','autumn'] },
  { id: 'strawberry', name: '草莓',   emoji: '🍓', requiredScore: 100, growthStages: ['🌱','🌿','🌸','🍓'], harvestScore: 80,  color: 'text-rose-600',   bgColor: 'bg-rose-50',   season: ['spring'] },
  { id: 'cherry',     name: '樱桃',   emoji: '🍒', requiredScore: 200, growthStages: ['🌱','🌸','🌺','🍒'], harvestScore: 120, color: 'text-red-600',    bgColor: 'bg-red-50',    season: ['spring'] },
  { id: 'pea',        name: '豌豆',   emoji: '🫛', requiredScore: 50,  growthStages: ['🌱','🌿','🌾','🫛'], harvestScore: 50,  color: 'text-green-600',  bgColor: 'bg-green-50',  season: ['spring'] },
  { id: 'watermelon', name: '西瓜',   emoji: '🍉', requiredScore: 150, growthStages: ['🌱','🌿','🌾','🍉'], harvestScore: 150, color: 'text-green-600',  bgColor: 'bg-green-50',  season: ['summer'] },
  { id: 'corn',       name: '玉米',   emoji: '🌽', requiredScore: 100, growthStages: ['🌱','🌿','🌾','🌽'], harvestScore: 100, color: 'text-yellow-600', bgColor: 'bg-yellow-50', season: ['summer'] },
  { id: 'tomato',     name: '番茄',   emoji: '🍅', requiredScore: 80,  growthStages: ['🌱','🌿','🌸','🍅'], harvestScore: 70,  color: 'text-red-600',   bgColor: 'bg-red-50',    season: ['summer'] },
  { id: 'peach',      name: '桃子',   emoji: '🍑', requiredScore: 300, growthStages: ['🌱','🌸','🌺','🍑'], harvestScore: 180, color: 'text-orange-500', bgColor: 'bg-orange-50', season: ['summer'] },
  { id: 'pumpkin',    name: '南瓜',   emoji: '🎃', requiredScore: 120, growthStages: ['🌱','🌿','🌾','🎃'], harvestScore: 110, color: 'text-orange-600', bgColor: 'bg-orange-50', season: ['autumn'] },
  { id: 'grape',      name: '葡萄',   emoji: '🍇', requiredScore: 250, growthStages: ['🌱','🌿','🌸','🍇'], harvestScore: 160, color: 'text-violet-600', bgColor: 'bg-violet-50', season: ['autumn'] },
  { id: 'apple',      name: '苹果',   emoji: '🍎', requiredScore: 200, growthStages: ['🌱','🌸','🌺','🍎'], harvestScore: 130, color: 'text-red-600',   bgColor: 'bg-red-50',    season: ['autumn'] },
  { id: 'mushroom',   name: '蘑菇',   emoji: '🍄', requiredScore: 180, growthStages: ['🌱','🌿','🍄‍🟫','🍄'], harvestScore: 140, color: 'text-amber-700', bgColor: 'bg-amber-50', season: ['autumn'] },
  { id: 'cabbage',    name: '白菜',   emoji: '🥬', requiredScore: 0,   growthStages: ['🌱','🌿','🥦','🥬'],  harvestScore: 40,  color: 'text-green-700', bgColor: 'bg-green-50',  season: ['winter'] },
  { id: 'radish',     name: '萝卜',   emoji: '🫚', requiredScore: 60,  growthStages: ['🌱','🌿','🌾','🫚'],  harvestScore: 60,  color: 'text-rose-500',  bgColor: 'bg-rose-50',   season: ['winter'] },
  { id: 'snowflower', name: '雪莲花', emoji: '🌼', requiredScore: 400, growthStages: ['🌱','❄️','🌨️','🌼'],  harvestScore: 250, color: 'text-sky-600',   bgColor: 'bg-sky-50',    season: ['winter'] },
  { id: 'pine',       name: '松果',   emoji: '🌲', requiredScore: 500, growthStages: ['🌱','🌿','🎄','🌲'],  harvestScore: 300, color: 'text-emerald-700',bgColor: 'bg-emerald-50',season: ['winter'] },
];

const STUDY_ACTIONS = [
  { id: 'checkin', label: '每日签到',   score: 10,  icon: 'ri-calendar-check-line', color: 'text-green-600',  bg: 'bg-green-50' },
  { id: 'post',    label: '发布动态',   score: 20,  icon: 'ri-quill-pen-line',      color: 'text-blue-600',   bg: 'bg-blue-50' },
  { id: 'help',    label: '帮助他人',   score: 50,  icon: 'ri-hand-heart-line',     color: 'text-rose-600',   bg: 'bg-rose-50' },
  { id: 'upload',  label: '上传文档',   score: 30,  icon: 'ri-file-upload-line',    color: 'text-amber-600',  bg: 'bg-amber-50' },
  { id: 'streak',  label: '连续7天登录',score: 100, icon: 'ri-fire-line',           color: 'text-orange-600', bg: 'bg-orange-50' },
  { id: 'donate',  label: '爱心捐款',   score: 200, icon: 'ri-heart-fill',          color: 'text-red-600',    bg: 'bg-red-50' },
];

const getCurrentSeason = (): Season => {
  const m = new Date().getMonth() + 1;
  if (m >= 3 && m <= 5) return 'spring';
  if (m >= 6 && m <= 8) return 'summer';
  if (m >= 9 && m <= 11) return 'autumn';
  return 'winter';
};

const buildInitialPlots = (count: number): PlotState[] =>
  Array.from({ length: count }, (_, i) => ({ id: i, cropId: null, stage: 0, plantedAt: null, watered: false }));

const DEMO_SEED: Partial<PlotState>[] = [
  { cropId: 'carrot',     stage: 4, watered: true },
  { cropId: 'tomato',     stage: 3, watered: true },
  { cropId: 'corn',       stage: 2, watered: false },
  { cropId: 'pumpkin',    stage: 1, watered: false },
  {},
  { cropId: 'strawberry', stage: 4, watered: true },
  {},
  { cropId: 'grape',      stage: 2, watered: true },
  {},
];

export default function FarmGame({ currentUser, onOpenAuth, userScore, compact = false }: FarmGameProps) {
  const [currentSeason, setCurrentSeason] = useState<Season>(getCurrentSeason());
  const [totalHarvests, setTotalHarvests] = useState(8); // demo: already harvested 8
  const [plots, setPlots] = useState<PlotState[]>(() => {
    const base = buildInitialPlots(25);
    return base.map((p, i) => ({ ...p, ...(DEMO_SEED[i] || {}) }));
  });
  const [selectedPlot, setSelectedPlot] = useState<number | null>(null);
  const [harvestCount, setHarvestCount] = useState<Record<string, number>>({ carrot: 3, tomato: 1, strawberry: 2, grape: 1, corn: 1 });
  const [showPlantModal, setShowPlantModal] = useState(false);
  const [showLevelModal, setShowLevelModal] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);
  const [farmScore, setFarmScore] = useState(userScore);
  const [doneActions, setDoneActions] = useState<Set<string>>(new Set(['checkin']));
  const [activeTab, setActiveTab] = useState<'farm' | 'tasks' | 'unlock'>('farm');
  const [justLeveledUp, setJustLeveledUp] = useState(false);

  useEffect(() => { setFarmScore(userScore); }, [userScore]);

  const currentLevel = FARM_LEVELS.slice().reverse().find(l => totalHarvests >= l.requiredHarvests) || FARM_LEVELS[0];
  const nextLevel = FARM_LEVELS.find(l => l.level === currentLevel.level + 1);
  const activePlotCount = currentLevel.plotCount;
  const levelProgress = nextLevel
    ? Math.min(100, ((totalHarvests - currentLevel.requiredHarvests) / (nextLevel.requiredHarvests - currentLevel.requiredHarvests)) * 100)
    : 100;

  const seasonCrops = ALL_CROPS.filter(c => c.season.includes(currentSeason));
  const unlockedCrops = seasonCrops.filter(c => farmScore >= c.requiredScore);
  const seasonInfo = SEASON_CONFIG[currentSeason];

  const showNotif = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 2800);
  };

  const handlePlotClick = (plotId: number) => {
    if (plotId >= activePlotCount) return; // locked plot
    if (!currentUser) { onOpenAuth(); return; }
    const plot = plots[plotId];
    if (plot.stage === 4) {
      const crop = ALL_CROPS.find(c => c.id === plot.cropId);
      if (crop) {
        const newHarvests = totalHarvests + 1;
        setTotalHarvests(newHarvests);
        setHarvestCount(prev => ({ ...prev, [crop.id]: (prev[crop.id] || 0) + 1 }));
        const bonus = currentLevel.level >= 3 ? crop.harvestScore * 2 : crop.harvestScore;
        setFarmScore(prev => prev + bonus);
        setPlots(prev => prev.map(p => p.id === plotId ? { ...p, cropId: null, stage: 0, watered: false, plantedAt: null } : p));
        // Check level up
        const newLevel = FARM_LEVELS.slice().reverse().find(l => newHarvests >= l.requiredHarvests);
        if (newLevel && newLevel.level > currentLevel.level) {
          setJustLeveledUp(true);
          setTimeout(() => setJustLeveledUp(false), 3000);
          showNotif(`🎉 农场升级！升到 ${newLevel.emoji} ${newLevel.name}！解锁 ${newLevel.plotCount} 块农田！`);
        } else {
          showNotif(`收获了 ${crop.emoji} ${crop.name}！+${bonus} 积分${currentLevel.level >= 3 ? ' (双倍!)' : ''} 🎉`);
        }
      }
    } else if (plot.stage === 0) {
      setSelectedPlot(plotId);
      setShowPlantModal(true);
    } else if (!plot.watered) {
      setPlots(prev => prev.map(p => p.id === plotId ? { ...p, watered: true } : p));
      showNotif('浇水成功！蔬菜成长更快了 💧');
    } else {
      showNotif('已经浇过水了，等待成长中... 🌱');
    }
  };

  const handlePlant = (cropId: string) => {
    if (selectedPlot === null) return;
    const crop = ALL_CROPS.find(c => c.id === cropId);
    if (!crop) return;
    if (farmScore < crop.requiredScore) {
      showNotif(`积分不足！需要 ${crop.requiredScore} 积分`);
      setShowPlantModal(false);
      return;
    }
    setPlots(prev => prev.map(p =>
      p.id === selectedPlot ? { ...p, cropId, stage: 1, plantedAt: Date.now(), watered: false } : p
    ));
    setShowPlantModal(false);
    setSelectedPlot(null);
    showNotif(`种下了 ${crop.emoji} ${crop.name}！记得浇水哦 🌱`);
  };

  const handleStudyAction = (actionId: string, score: number) => {
    if (!currentUser) { onOpenAuth(); return; }
    if (doneActions.has(actionId)) { showNotif('今天已经完成过这个任务了！'); return; }
    setDoneActions(prev => new Set([...prev, actionId]));
    setFarmScore(prev => prev + score);
    setPlots(prev => {
      const growable = prev.filter((p, i) => i < activePlotCount && p.cropId && p.stage > 0 && p.stage < 4);
      if (growable.length === 0) return prev;
      const target = growable[Math.floor(Math.random() * growable.length)];
      return prev.map(p => p.id === target.id ? { ...p, stage: Math.min(p.stage + 1, 4) as PlotState['stage'] } : p);
    });
    showNotif(`完成学习任务！+${score} 积分，农场蔬菜成长了！🌱`);
  };

  const getPlotDisplay = (plot: PlotState) => {
    if (plot.stage === 0) return { emoji: '🟫', action: '空地' };
    const crop = ALL_CROPS.find(c => c.id === plot.cropId);
    if (!crop) return { emoji: '🟫', action: '空地' };
    if (plot.stage === 4) return { emoji: crop.emoji, action: '收获！' };
    return { emoji: crop.growthStages[plot.stage - 1], action: plot.watered ? '成长中' : '需浇水' };
  };

  // Grid columns based on plot count
  const getGridCols = (count: number) => {
    if (count <= 9) return 'grid-cols-3';
    if (count <= 12) return 'grid-cols-4';
    if (count <= 16) return 'grid-cols-4';
    if (count <= 20) return 'grid-cols-5';
    return 'grid-cols-5';
  };

  if (compact) {
    return (
      <div className="rounded-2xl overflow-hidden border border-gray-100">
        <div className="p-3 text-white" style={{ background: `linear-gradient(135deg, ${seasonInfo.gradFrom}, ${seasonInfo.gradTo})` }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg">{seasonInfo.emoji}</span>
              <div>
                <p className="font-black text-sm">学习农场 · {seasonInfo.label}</p>
                <p className="text-white/80 text-xs">{currentLevel.emoji} {currentLevel.name} · Lv.{currentLevel.level}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-white/70 text-[10px]">积分</p>
              <p className="text-xl font-black">{farmScore}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-3">
          <div className={`rounded-xl p-2 grid ${getGridCols(activePlotCount)} gap-1.5`} style={{ background: seasonInfo.bg }}>
            {plots.slice(0, activePlotCount).map((plot, idx) => {
              const display = getPlotDisplay(plot);
              const isReady = plot.stage === 4;
              const needsWater = plot.stage > 0 && plot.stage < 4 && !plot.watered;
              return (
                <button key={idx} onClick={() => handlePlotClick(idx)}
                  className={`relative aspect-square rounded-lg flex flex-col items-center justify-center gap-0.5 transition-all cursor-pointer border ${
                    isReady ? 'bg-yellow-100 border-yellow-400 animate-pulse' :
                    plot.stage === 0 ? 'bg-amber-100/60 border-amber-300/50 hover:bg-amber-100' :
                    needsWater ? 'bg-green-100 border-orange-300' : 'bg-green-100 border-green-300'
                  }`}>
                  {isReady && <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-yellow-400 rounded-full flex items-center justify-center"><i className="ri-star-fill text-white text-[6px]"></i></div>}
                  {needsWater && <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-orange-400 rounded-full flex items-center justify-center"><span className="text-[6px]">💧</span></div>}
                  <span className="text-base leading-none">{display.emoji}</span>
                  <span className="text-[8px] text-gray-600 font-medium">{display.action}</span>
                </button>
              );
            })}
          </div>
          {notification && (
            <div className="mt-2 bg-green-50 rounded-lg px-3 py-1.5 flex items-center gap-1.5 text-green-700 text-xs">
              <i className="ri-checkbox-circle-line text-green-500 text-sm"></i>
              {notification}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
      {/* Level Up Banner */}
      {justLeveledUp && (
        <div className="bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-3 text-white flex items-center gap-3">
          <span className="text-2xl">🎉</span>
          <div>
            <p className="font-black text-sm">农场升级啦！</p>
            <p className="text-white/90 text-xs">解锁了更多农田，继续努力学习！</p>
          </div>
        </div>
      )}

      {/* Season + Level Header */}
      <div className="p-5 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${seasonInfo.gradFrom}, ${seasonInfo.gradTo})` }}>
        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-7xl opacity-20">{seasonInfo.emoji}</div>
        <div className="relative z-10">
          {/* Level Badge */}
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={() => setShowLevelModal(true)}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 transition-all rounded-full px-3 py-1.5 cursor-pointer"
            >
              <span className="text-lg">{currentLevel.emoji}</span>
              <div>
                <p className="font-black text-sm leading-none">{currentLevel.name}</p>
                <p className="text-white/80 text-[10px]">Lv.{currentLevel.level} · {activePlotCount}块农田</p>
              </div>
              <i className="ri-arrow-right-s-line text-white/70 text-sm"></i>
            </button>
            {nextLevel && (
              <div className="flex-1 max-w-[120px]">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-white/70 text-[10px]">升级进度</span>
                  <span className="text-white/90 text-[10px] font-bold">{totalHarvests}/{nextLevel.requiredHarvests}</span>
                </div>
                <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${levelProgress}%` }}></div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 mb-1">
            <span className="text-xl">{seasonInfo.emoji}</span>
            <span className="font-black text-base">我的学习农场 · {seasonInfo.label}</span>
          </div>
          <p className="text-white/80 text-xs mb-3">{seasonInfo.desc} · 多学习蔬菜成长更快！</p>
          <div className="flex items-center gap-5">
            <div><p className="text-white/70 text-xs">农场积分</p><p className="text-2xl font-black">{farmScore}</p></div>
            <div><p className="text-white/70 text-xs">累计收获</p><p className="text-2xl font-black">{totalHarvests}</p></div>
            <div><p className="text-white/70 text-xs">本季解锁</p><p className="text-2xl font-black">{unlockedCrops.length}/{seasonCrops.length}</p></div>
          </div>
        </div>
        {/* Season Switcher */}
        <div className="flex gap-1.5 mt-3 flex-wrap">
          {(Object.keys(SEASON_CONFIG) as Season[]).map((s) => (
            <button key={s} onClick={() => setCurrentSeason(s)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                currentSeason === s ? 'bg-white text-gray-800' : 'bg-white/20 text-white hover:bg-white/30'
              }`}>
              {SEASON_CONFIG[s].emoji} {SEASON_CONFIG[s].label}
            </button>
          ))}
        </div>
      </div>

      {notification && (
        <div className="bg-green-50 border-b border-green-100 px-4 py-2.5 flex items-center gap-2 text-green-700 text-sm font-medium">
          <i className="ri-checkbox-circle-line text-green-500"></i>
          {notification}
        </div>
      )}

      {/* Inner Tabs */}
      <div className="flex border-b border-gray-100">
        {[
          { key: 'farm',   label: '🌾 农田' },
          { key: 'tasks',  label: '📚 学习任务' },
          { key: 'unlock', label: '🔓 解锁进度' },
        ].map((tab) => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key as typeof activeTab)}
            className={`flex-1 py-2.5 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab.key ? 'text-green-600 border-b-2 border-green-500' : 'text-gray-500 hover:text-gray-700'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="p-4">
        {activeTab === 'farm' && (
          <>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500">点击空地种植 · 点击作物浇水/收获</p>
              <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">{activePlotCount}块农田</span>
            </div>

            {/* Farm Grid */}
            <div className={`rounded-2xl p-3 grid ${getGridCols(activePlotCount)} gap-2 mb-4`} style={{ background: seasonInfo.bg }}>
              {plots.slice(0, 25).map((plot, idx) => {
                const isLocked = idx >= activePlotCount;
                const display = getPlotDisplay(plot);
                const isReady = plot.stage === 4;
                const isEmpty = plot.stage === 0;
                const needsWater = plot.stage > 0 && plot.stage < 4 && !plot.watered;
                if (isLocked) {
                  return (
                    <button key={idx} onClick={() => setShowLevelModal(true)}
                      className="relative aspect-square rounded-xl flex flex-col items-center justify-center gap-1 bg-black/20 border-2 border-white/10 cursor-pointer hover:bg-black/30 transition-all">
                      <i className="ri-lock-line text-white/50 text-lg"></i>
                      <span className="text-[8px] text-white/50 font-medium">升级解锁</span>
                    </button>
                  );
                }
                return (
                  <button key={idx} onClick={() => handlePlotClick(idx)}
                    className={`relative aspect-square rounded-xl flex flex-col items-center justify-center gap-1 transition-all cursor-pointer border-2 ${
                      isReady ? 'bg-yellow-100 border-yellow-400 animate-pulse' :
                      isEmpty ? 'bg-amber-100/60 border-amber-300/50 hover:bg-amber-100 hover:border-amber-400' :
                      needsWater ? 'bg-green-100 border-orange-300 hover:border-orange-400' :
                      'bg-green-100 border-green-300'
                    }`}>
                    {isReady && <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center"><i className="ri-star-fill text-white text-[8px]"></i></div>}
                    {needsWater && <div className="absolute -top-1 -right-1 w-4 h-4 bg-orange-400 rounded-full flex items-center justify-center"><span className="text-[8px]">💧</span></div>}
                    <span className="text-2xl leading-none">{display.emoji}</span>
                    <span className="text-[9px] text-gray-600 font-medium leading-tight text-center px-1">{display.action}</span>
                  </button>
                );
              })}
            </div>

            {/* Harvest Basket */}
            <div>
              <h4 className="text-xs font-bold text-gray-700 mb-2">收获篮 🧺</h4>
              <div className="flex flex-wrap gap-1.5">
                {ALL_CROPS.filter(c => (harvestCount[c.id] || 0) > 0).map((crop) => (
                  <div key={crop.id} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${crop.bgColor} ${crop.color}`}>
                    <span>{crop.emoji}</span><span>{crop.name}</span><span className="font-black">×{harvestCount[crop.id]}</span>
                  </div>
                ))}
                {Object.values(harvestCount).every(v => v === 0) && <p className="text-xs text-gray-400">还没有收获，快去种植吧！</p>}
              </div>
            </div>
          </>
        )}

        {activeTab === 'tasks' && (
          <>
            <div className="bg-green-50 rounded-xl p-3 mb-3 flex items-start gap-2">
              <i className="ri-plant-line text-green-600 text-sm mt-0.5 flex-shrink-0"></i>
              <p className="text-green-700 text-xs leading-relaxed">完成学习任务赚积分，积分越多解锁的蔬菜越多！每完成一个任务，农田里随机一株作物自动成长一阶 🌱</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {STUDY_ACTIONS.map((action) => {
                const done = doneActions.has(action.id);
                return (
                  <button key={action.id} onClick={() => handleStudyAction(action.id, action.score)} disabled={done}
                    className={`flex items-center gap-2 p-2.5 rounded-xl text-left transition-all cursor-pointer border ${
                      done ? 'bg-gray-50 border-gray-100 opacity-60' : `${action.bg} border-current/10 hover:scale-[1.02]`
                    }`}>
                    <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${done ? 'bg-gray-100' : action.bg}`}>
                      <i className={`${action.icon} text-sm ${done ? 'text-gray-400' : action.color}`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-semibold truncate ${done ? 'text-gray-400' : 'text-gray-700'}`}>{action.label}</p>
                      <p className={`text-[10px] font-bold ${done ? 'text-gray-300' : 'text-amber-500'}`}>{done ? '已完成 ✓' : `+${action.score}分 🌱`}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {activeTab === 'unlock' && (
          <>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {(Object.keys(SEASON_CONFIG) as Season[]).map((s) => (
                <button key={s} onClick={() => setCurrentSeason(s)}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border ${
                    currentSeason === s ? 'bg-green-600 text-white border-green-600' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}>
                  {SEASON_CONFIG[s].emoji} {SEASON_CONFIG[s].label}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-2">
              {seasonCrops.map((crop) => {
                const unlocked = farmScore >= crop.requiredScore;
                const progress = crop.requiredScore === 0 ? 100 : Math.min(100, (farmScore / crop.requiredScore) * 100);
                return (
                  <div key={crop.id} className="flex items-center gap-2">
                    <span className={`text-lg ${unlocked ? '' : 'grayscale opacity-40'}`}>{crop.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className={`text-xs font-medium ${unlocked ? 'text-gray-700' : 'text-gray-400'}`}>{crop.name}</span>
                        <span className={`text-[10px] font-bold ${unlocked ? 'text-green-600' : 'text-gray-400'}`}>
                          {unlocked ? '已解锁 ✓' : `${farmScore}/${crop.requiredScore}`}
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-500 ${unlocked ? 'bg-green-500' : 'bg-amber-400'}`} style={{ width: `${progress}%` }}></div>
                      </div>
                    </div>
                    <span className={`text-[10px] font-bold ${crop.color} ${crop.bgColor} px-1.5 py-0.5 rounded-full`}>+{crop.harvestScore}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Plant Modal */}
      {showPlantModal && (
        <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl">
            <div className="px-5 py-4 text-white flex items-center justify-between" style={{ background: `linear-gradient(135deg, ${seasonInfo.gradFrom}, ${seasonInfo.gradTo})` }}>
              <div>
                <h3 className="font-black text-base">{seasonInfo.emoji} {seasonInfo.label}作物</h3>
                <p className="text-white/80 text-xs">当前积分：{farmScore}</p>
              </div>
              <button onClick={() => setShowPlantModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 cursor-pointer">
                <i className="ri-close-line text-white"></i>
              </button>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3 max-h-80 overflow-y-auto">
              {seasonCrops.map((crop) => {
                const unlocked = farmScore >= crop.requiredScore;
                return (
                  <button key={crop.id} onClick={() => unlocked && handlePlant(crop.id)} disabled={!unlocked}
                    className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                      unlocked ? `${crop.bgColor} border-current/20 hover:scale-[1.03] ${crop.color}` : 'bg-gray-50 border-gray-100 opacity-50 cursor-not-allowed'
                    }`}>
                    <span className={`text-3xl ${!unlocked ? 'grayscale' : ''}`}>{crop.emoji}</span>
                    <span className={`text-xs font-bold ${unlocked ? crop.color : 'text-gray-400'}`}>{crop.name}</span>
                    <span className={`text-[10px] ${unlocked ? 'text-green-600 font-semibold' : 'text-gray-400'}`}>
                      {unlocked ? `收获 +${crop.harvestScore}分` : `需要${crop.requiredScore}分`}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Level System Modal */}
      {showLevelModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-4 text-white flex items-center justify-between">
              <div>
                <h3 className="font-black text-base">农场等级系统</h3>
                <p className="text-white/80 text-xs">收获越多，农场越大！</p>
              </div>
              <button onClick={() => setShowLevelModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 cursor-pointer">
                <i className="ri-close-line text-white"></i>
              </button>
            </div>
            <div className="p-5 flex flex-col gap-3">
              {FARM_LEVELS.map((lvl) => {
                const isCurrentLevel = lvl.level === currentLevel.level;
                const isUnlocked = totalHarvests >= lvl.requiredHarvests;
                return (
                  <div key={lvl.level} className={`flex items-center gap-3 p-3 rounded-2xl border-2 transition-all ${
                    isCurrentLevel ? 'border-amber-400 bg-amber-50' :
                    isUnlocked ? 'border-green-200 bg-green-50' : 'border-gray-100 bg-gray-50 opacity-60'
                  }`}>
                    <div className={`w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0 text-2xl ${
                      isCurrentLevel ? 'bg-amber-100' : isUnlocked ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      {lvl.emoji}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-black text-sm ${isCurrentLevel ? 'text-amber-700' : isUnlocked ? 'text-green-700' : 'text-gray-400'}`}>
                          Lv.{lvl.level} {lvl.name}
                        </span>
                        {isCurrentLevel && <span className="text-[10px] bg-amber-400 text-white px-2 py-0.5 rounded-full font-bold">当前</span>}
                        {isUnlocked && !isCurrentLevel && <span className="text-[10px] bg-green-500 text-white px-2 py-0.5 rounded-full font-bold">已解锁</span>}
                      </div>
                      <p className={`text-xs mt-0.5 ${isCurrentLevel ? 'text-amber-600' : isUnlocked ? 'text-green-600' : 'text-gray-400'}`}>{lvl.perks}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">需要收获 {lvl.requiredHarvests} 次</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`text-lg font-black ${isCurrentLevel ? 'text-amber-600' : isUnlocked ? 'text-green-600' : 'text-gray-300'}`}>
                        {lvl.plotCount}
                      </p>
                      <p className="text-[10px] text-gray-400">块农田</p>
                    </div>
                  </div>
                );
              })}
              <div className="bg-amber-50 rounded-xl p-3 flex items-center gap-2">
                <i className="ri-information-line text-amber-500 text-sm flex-shrink-0"></i>
                <p className="text-amber-700 text-xs">当前已收获 <strong>{totalHarvests}</strong> 次，{nextLevel ? `还需 ${nextLevel.requiredHarvests - totalHarvests} 次升级到 ${nextLevel.name}` : '已达最高等级！'}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
